import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Rss, ScanSearch, MessageSquare, ArrowRight, Star, Check } from "lucide-react";
import { AppHeader, SourceNote } from "@/components/Layout";
import { PhonePreview } from "@/components/PhonePreview";
import { Reviews } from "@/components/Reviews";
import { useSeo } from "@/lib/seo";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.09 } }),
};

const STEPS = [
  {
    n: "01",
    Icon: Rss,
    title: "FSANZ publishes a recall",
    body: "We watch the official Food Standards Australia New Zealand feed every 5 minutes. Nothing else counts as a source.",
  },
  {
    n: "02",
    Icon: ScanSearch,
    title: "We read it and check the facts",
    body: "The official notice is parsed for the product, the reason and where it was sold, then checked before anything is sent.",
  },
  {
    n: "03",
    Icon: MessageSquare,
    title: "You get one text",
    body: "A single SMS naming the product, the risk and your state, with a link to the official notice. If you told us about an allergy, it leads with that.",
    feature: true,
  },
];

export default function Landing() {
  useSeo({
    title: "Food Recall Alerts Australia | Australian food recalls by SMS",
    description:
      "Food recall alerts for Australia, sent by SMS. We monitor every official Australian food recall from Food Standards Australia New Zealand (FSANZ) and text you the moment one matches your state and your allergies.",
    path: "/",
  });
  return (
    <div className="min-h-screen bg-white">
      <AppHeader />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="pointer-events-none absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-red-50 blur-3xl" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-5 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:py-24">
          <motion.div initial="hidden" animate="show" className="max-w-2xl">
            <motion.div variants={fadeUp} custom={0}>
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-600 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-600" />
                </span>
                <span className="hidden sm:inline">Monitoring Food Standards Australia &amp; New Zealand recalls live</span>
                <span className="sm:hidden">Monitoring FSANZ recalls live</span>
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              custom={1}
              className="mt-6 text-5xl font-extrabold leading-[0.95] tracking-tighter text-slate-900 sm:text-6xl lg:text-7xl"
            >
              Never miss an
              <br />
              Australian
              <br />
              <span className="text-red-700">food recall.</span>
            </motion.h1>

            <motion.p variants={fadeUp} custom={2} className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg">
              Food recall alerts for Australia, by SMS. We watch every official Australian food recall so you
              don't have to. When one affects your state or your allergies, you get a single text message.
              No app to check, no feed to follow.
            </motion.p>

            <motion.div variants={fadeUp} custom={3} className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <Link
                to="/join"
                data-testid="get-alerts-cta"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-red-700 px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 sm:py-3.5"
              >
                Get recall alerts
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/alerts"
                data-testid="browse-recalls-link"
                className="rounded-full border border-slate-300 px-7 py-4 text-center text-base font-medium text-slate-800 transition-colors hover:bg-slate-50 sm:py-3.5"
              >
                Browse recent recalls
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} custom={4} className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <Check className="h-4 w-4 text-emerald-600" /> Free to browse every recall
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="h-4 w-4 text-emerald-600" /> SMS alerts from A$4.99/month
              </span>
            </motion.div>

            <motion.p variants={fadeUp} custom={5} className="mt-5 text-sm text-slate-600">
              Already signed up?{" "}
              <Link to="/login" data-testid="landing-login-link" className="font-semibold text-red-700 hover:underline">
                Login
              </Link>
            </motion.p>
          </motion.div>

          <PhonePreview />
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-slate-200 bg-slate-50" data-testid="how-it-works">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-20">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl">
            <p className="label-caps">How it works</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tighter text-slate-900 sm:text-4xl lg:text-5xl">
              Three steps. Then we stay out of your way.
            </h2>
          </motion.div>

          <div className="mt-10 grid gap-5 sm:mt-12 lg:grid-cols-3">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className={`flex h-full flex-col rounded-2xl border p-6 transition-transform duration-200 hover:-translate-y-1 sm:p-7 ${
                  s.feature
                    ? "border-slate-900 bg-slate-900 text-white lg:row-span-1"
                    : "border-slate-200 bg-white"
                }`}
                data-testid={`step-${s.n}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`font-mono text-xs font-semibold ${s.feature ? "text-white/50" : "text-slate-400"}`}>
                    {s.n}
                  </span>
                  <s.Icon className={`h-5 w-5 ${s.feature ? "text-red-400" : "text-red-700"}`} />
                </div>
                <h3 className={`mt-6 text-lg font-semibold tracking-tight sm:mt-8 sm:text-xl ${s.feature ? "text-white" : "text-slate-900"}`}>
                  {s.title}
                </h3>
                <p className={`mt-3 flex-1 text-sm leading-relaxed ${s.feature ? "text-white/70" : "text-slate-600"}`}>
                  {s.body}
                </p>
                {s.feature && (
                  <p className="sms-preview mt-6 rounded-xl bg-white/10 p-3 text-[13px] leading-snug text-white/90">
                    PEANUT ALERT: Mr Chen's Szechuan Chilli Oil 250g. Undeclared peanut. National recall.
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Keeping families safe */}
      <section className="border-t border-slate-200 bg-white" data-testid="families-safe-section">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:gap-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <p className="label-caps">Why it matters</p>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tighter text-slate-900 sm:text-4xl lg:text-5xl">
              Helping protect Australian families, one alert at a time.
            </h2>
            <p className="mt-6 text-base text-slate-600 sm:text-lg">
              Food recalls can happen after a product is already in your fridge, pantry or lunchbox.
            </p>
            <p className="mt-4 text-base text-slate-600 sm:text-lg">
              We make sure relevant recall alerts reach your family sooner - so you can check, act and
              keep the people you love safe.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.35)]"
          >
            <picture>
              <source
                type="image/webp"
                srcSet="/img/family-kitchen-800.webp 800w, /img/family-kitchen-1312.webp 1312w"
                sizes="(min-width: 1024px) 46vw, 92vw"
              />
              <img
                src="/img/family-kitchen-800.jpg"
                srcSet="/img/family-kitchen-800.jpg 800w, /img/family-kitchen-1312.jpg 1312w"
                sizes="(min-width: 1024px) 46vw, 92vw"
                width="1312"
                height="736"
                loading="lazy"
                decoding="async"
                alt="Australian parent in a kitchen checking a food label after receiving a food recall alert SMS"
                data-testid="families-safe-image"
                className="h-auto w-full"
              />
            </picture>
            <span className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-slate-900/10 via-transparent to-transparent" />
          </motion.div>
        </div>
      </section>

      <Reviews />

      {/* Closing CTA */}
      <section className="border-t border-slate-200 bg-slate-900">
        <div className="mx-auto max-w-3xl px-5 py-14 text-center sm:py-20">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <div className="mb-6 flex justify-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <h2 className="text-3xl font-bold tracking-tighter text-white sm:text-5xl">
              The next recall is already coming.
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-white/70">
              Australia averages a food recall every week. Be told about the ones that matter to your household.
            </p>
            <Link
              to="/join"
              data-testid="closing-cta"
              className="group mt-9 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-slate-900 transition-colors hover:bg-slate-100"
            >
              Get recall alerts
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      <footer className="px-5 py-10">
        <div className="mx-auto max-w-6xl space-y-4">
          <SourceNote />
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4 text-sm text-slate-500">
            <span className="font-display font-semibold text-slate-700" data-testid="footer-brand">
              Food Recall Alerts
              <a
                href="http://www.foodrecallalerts.com.au/"
                data-testid="footer-domain"
                className="ml-2 font-normal text-slate-400 hover:text-slate-700"
              >
                foodrecallalerts.com.au
              </a>
            </span>
            <Link to="/admin" data-testid="admin-link" className="hover:text-slate-900">Admin</Link>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-500">
            <Link to="/about" data-testid="footer-about" className="hover:text-slate-900">Our story</Link>
            <Link to="/pricing" data-testid="footer-pricing" className="hover:text-slate-900">Pricing</Link>
            <Link to="/alerts" data-testid="footer-alerts" className="hover:text-slate-900">Recall history</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-slate-900">Terms of Service</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-slate-900">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
